import { Menu, MenuItem, ListItemIcon, ListItemText, Switch, Typography, alpha } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";

const ACCOUNT_MENU_WIDTH_PX = 224;
const ACCOUNT_MENU_HEIGHT_PX = 300;
const ACCOUNT_MENU_HEADER_HEIGHT_PX = 61.5;
const ACCOUNT_MENU_ITEM_HEIGHT_PX = 48;
const ACCOUNT_MENU_ITEM_FONT_SIZE_PX = 11.375;
const ACCOUNT_MENU_ITEM_LINE_HEIGHT_PX = 17;
const ACCOUNT_MENU_ICON_SIZE_PX = 16;

const accountMenuListItemIconSx = (theme: Theme) => ({
  minWidth: `${ACCOUNT_MENU_ICON_SIZE_PX}px !important`,
  width: ACCOUNT_MENU_ICON_SIZE_PX,
  marginRight: 0,
  color: theme.app.accountMenu.iconColor,
  "& .MuiSvgIcon-root": {
    fontSize: ACCOUNT_MENU_ICON_SIZE_PX,
    width: ACCOUNT_MENU_ICON_SIZE_PX,
    height: ACCOUNT_MENU_ICON_SIZE_PX,
  },
});

const accountMenuItemSx = (theme: Theme) => ({
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  flex: "0 0 auto",
  height: ACCOUNT_MENU_ITEM_HEIGHT_PX,
  minHeight: ACCOUNT_MENU_ITEM_HEIGHT_PX,
  maxHeight: ACCOUNT_MENU_ITEM_HEIGHT_PX,
  gap: "20px",
  px: "16px",
  py: 0,
  overflow: "hidden",
  fontFamily: "var(--font-sans)",
  color: theme.app.accountMenu.primaryTextColor,
  "& .MuiListItemIcon-root": accountMenuListItemIconSx(theme),
  "& .MuiListItemText-root": {
    margin: 0,
    flex: "1 1 auto",
  },
  "& .MuiListItemText-primary": {
    fontFamily: "var(--font-sans)",
    fontWeight: 400,
    fontSize: `${ACCOUNT_MENU_ITEM_FONT_SIZE_PX}px`,
    lineHeight: `${ACCOUNT_MENU_ITEM_LINE_HEIGHT_PX}px`,
    color: "inherit",
  },
});

/** Section separator drawn on the item itself so hover covers the full row (no Divider gaps). */
const accountMenuSectionEndSx = (theme: Theme) => ({
  borderBottom: `1px solid ${theme.app.accountMenu.dividerColor}`,
});

const accountMenuSwitchSx = (theme: Theme) => ({
  ml: 0,
  width: 36,
  height: 20,
  padding: 0,
  flexShrink: 0,
  overflow: "hidden",
  "& .MuiSwitch-switchBase": {
    padding: "2px",
    "&.Mui-checked": {
      transform: "translateX(16px)",
    },
  },
  "& .MuiSwitch-thumb": {
    width: 16,
    height: 16,
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 10,
    backgroundColor: theme.app.accountMenu.switchTrackColor,
    opacity: 1,
  },
});

export default function AccountMenu({
  anchorEl,
  open,
  compact,
  profileLabel,
  firmName,
  email,
  isSystemAdmin,
  demoActive,
  darkModeActive,
  onClose,
  onProfile,
  onSettings,
  onToggleDemo,
  onToggleDarkMode,
  onSystemStatus,
  onLogout,
}: Readonly<{
  anchorEl: HTMLElement | null;
  open: boolean;
  compact: boolean;
  profileLabel: string;
  firmName: string | undefined;
  email: string | undefined;
  isSystemAdmin: boolean;
  demoActive: boolean;
  darkModeActive: boolean;
  onClose: () => void;
  onProfile: () => void;
  onSettings: () => void;
  onToggleDemo: () => void;
  onToggleDarkMode: () => void;
  onSystemStatus: () => void;
  onLogout: () => void;
}>) {
  const subtitle = firmName ?? email;

  return (
    <Menu
      anchorEl={anchorEl}
      id="account-menu"
      open={open}
      onClose={onClose}
      onClick={onClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: (theme: Theme) => ({
            mt: compact ? 0 : 1,
            mb: compact ? 1 : 0,
            width: ACCOUNT_MENU_WIDTH_PX,
            minWidth: ACCOUNT_MENU_WIDTH_PX,
            overflow: "hidden",
            borderRadius: "8px",
            backgroundColor: theme.app.accountMenu.background,
            boxShadow:
              "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
          }),
        },
        list: {
          sx: {
            py: 0,
            height: ACCOUNT_MENU_HEIGHT_PX,
            minHeight: ACCOUNT_MENU_HEIGHT_PX,
            maxHeight: ACCOUNT_MENU_HEIGHT_PX,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: compact ? "bottom" : "top" }}
      anchorOrigin={{ horizontal: "right", vertical: compact ? "top" : "bottom" }}
    >
      <MenuItem
        onClick={onProfile}
        sx={(theme) => ({
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          flex: "0 0 auto",
          gap: "4px",
          px: "16px",
          py: 0,
          height: ACCOUNT_MENU_HEADER_HEIGHT_PX,
          minHeight: ACCOUNT_MENU_HEADER_HEIGHT_PX,
          maxHeight: ACCOUNT_MENU_HEADER_HEIGHT_PX,
          backgroundColor: theme.app.accountMenu.headerBackground,
          borderBottom: `1px solid ${theme.app.accountMenu.dividerColor}`,
          "&:hover": { backgroundColor: theme.app.accountMenu.headerHoverBackground },
        })}
      >
        <Typography
          noWrap
          sx={(theme) => ({
            width: "100%",
            fontFamily: "var(--font-sans)",
            fontWeight: 600,
            fontSize: "10.4px",
            lineHeight: "16px",
            color: theme.app.accountMenu.primaryTextColor,
          })}
        >
          {profileLabel}
        </Typography>
        {subtitle && (
          <Typography
            noWrap
            component="div"
            sx={(theme) => ({
              width: "100%",
              fontFamily: "var(--font-sans)",
              fontWeight: 400,
              fontSize: "9.75px",
              lineHeight: "16px",
              color: theme.app.accountMenu.secondaryTextColor,
            })}
          >
            {subtitle}
          </Typography>
        )}
      </MenuItem>

      <MenuItem onClick={onSettings} sx={[accountMenuItemSx, accountMenuSectionEndSx]}>
        <ListItemIcon sx={accountMenuListItemIconSx}>
          <SettingsOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary="Settings" />
      </MenuItem>

      {isSystemAdmin && (
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            onToggleDemo();
          }}
          sx={accountMenuItemSx}
        >
          <ListItemIcon
            sx={(theme) => ({
              ...accountMenuListItemIconSx(theme),
              color: theme.app.accountMenu.demoIconColor,
            })}
          >
            <ScienceOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary="Demo Mode" />
          <Switch
            checked={demoActive}
            size="small"
            sx={accountMenuSwitchSx}
            onClick={(event) => event.stopPropagation()}
            onChange={() => {
              onToggleDemo();
            }}
          />
        </MenuItem>
      )}

      <MenuItem
        onClick={(event) => {
          // Keep the menu open so the switch's on/off state is visible after toggling —
          // stop the click from bubbling into the Menu's own onClick={onClose}.
          event.stopPropagation();
          onToggleDarkMode();
        }}
        sx={[accountMenuItemSx, accountMenuSectionEndSx]}
      >
        <ListItemIcon sx={accountMenuListItemIconSx}>
          {darkModeActive ? <LightModeIcon /> : <DarkModeIcon />}
        </ListItemIcon>
        <ListItemText primary="Dark Mode" />
        <Switch
          checked={darkModeActive}
          size="small"
          sx={accountMenuSwitchSx}
          onClick={(event) => event.stopPropagation()}
          onChange={() => {
            onToggleDarkMode();
          }}
        />
      </MenuItem>

      <MenuItem onClick={onSystemStatus} sx={[accountMenuItemSx, accountMenuSectionEndSx]}>
        <ListItemIcon sx={accountMenuListItemIconSx}>
          <BarChartOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary="System Status" />
      </MenuItem>

      <MenuItem
        onClick={onLogout}
        sx={[
          accountMenuItemSx,
          (theme) => ({
            color: theme.app.accountMenu.logoutColor,
            "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.08) },
          }),
        ]}
      >
        <ListItemIcon
          sx={(theme) => ({
            ...accountMenuListItemIconSx(theme),
            color: theme.app.accountMenu.logoutColor,
          })}
        >
          <LogoutIcon />
        </ListItemIcon>
        <ListItemText primary="Logout" />
      </MenuItem>
    </Menu>
  );
}
